import { useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/config/siteConfig";

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="relative py-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
            <span>← Back to Home</span>
          </Link>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Contact <span className="text-gradient">CityLink</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl">
            Get in touch with our offices in Johannesburg, Kempton Park, Maputo and Matola. We're here to help with your travel needs.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Get In Touch</h2>
        <p className="text-lg text-muted-foreground mb-12 max-w-2xl">
          Have questions about our services? We're here to help. Reach out to us anytime.
        </p>

        {/* Office Locations - Consolidated Blocks */}
        <div className="grid md:grid-cols-2 gap-8">
          {siteConfig.locations.map((location) => (
            <div
              key={location.id}
              className="bg-card rounded-2xl border border-border/50 overflow-hidden hover:border-primary/50 transition-all duration-300"
            >
              {/* Header with Map */}
              <div className="relative h-64 bg-gradient-to-br from-primary/10 to-secondary/10 overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://maps.google.com/maps?q=${location.lat},${location.lng}&z=15&output=embed`}
                />
              </div>

              {/* Consolidated Info Block */}
              <div className="p-8">
                <h3 className="font-display text-2xl font-bold mb-6 text-foreground">
                  {location.name}
                </h3>

                {/* Address Block */}
                <div className="mb-6 pb-6 border-b border-border/30">
                  <div className="flex gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground font-medium mb-1">Address</p>
                      <p className="font-medium text-foreground whitespace-pre-line">{location.address}</p>
                      <Button asChild variant="link" className="p-0 h-auto mt-2">
                        <a href={location.mapUrl} target="_blank" rel="noopener noreferrer">
                          View on Map →
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Phone Block */}
                <div className="mb-6 pb-6 border-b border-border/30">
                  <div className="flex gap-3">
                    <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground font-medium mb-2">Phone</p>
                      <a
                        href={`tel:${location.phone.replace(/\s/g, '')}`}
                        className="text-lg font-bold text-primary hover:text-primary/80 transition-colors block mb-3"
                      >
                        {location.phone}
                      </a>
                      <Button asChild size="sm">
                        <a href={`tel:${location.phone.replace(/\s/g, '')}`}>Call Now</a>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Email Block */}
                <div className="mb-2">
                  <div className="flex gap-3">
                    <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="w-full">
                      <p className="text-sm text-muted-foreground font-medium mb-2">Email</p>
                      <a
                        href={`mailto:${location.email}`}
                        className="font-medium text-primary hover:text-primary/80 transition-colors break-all block mb-3"
                      >
                        {location.email}
                      </a>
                      <Button asChild size="sm" variant="outline" className="w-full">
                        <a href={`mailto:${location.email}`}>Send Email</a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
